import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Bell, BellOff, ChevronLeft } from 'lucide-react';
import { num0 } from '../lib/numeros';

/**
 * EL DÍA POR DENTRO (ficha 21 del artefacto «Mi semana»).
 *
 *     «El horario del día: a qué hora se levanta, cada comida con su hora y sus macros, el
 *      intra, el entreno con su duración, el post, y a qué hora se acuesta. Arriba, los
 *      tres números que resumen: horas de sueño · comidas · hora del entreno.»
 *
 *     «Así ve su día colocado antes de que empiece: a qué hora come cada cosa y cuánto
 *      duerme. Es planificar para no fallar, no una agenda.»
 *
 * Las filas vienen ya en orden y con su hora del servidor: aquí no se decide qué va ni
 * dónde. La campana marca lo que le avisaría.
 */

// LOS COLORES DE SU LEYENDA, QUE SON TRES: naranja, marrón y gris (Francisco, 12-09, con
// la imagen de la leyenda delante). Aquí el cardio pautado salía azul cielo y el hueco de
// cardio extra también, dos colores que no están en ninguna parte de la app; en la barra de
// Mi semana ya se corrigieron y esta pantalla se había quedado atrás, así que el mismo
// cardio tenía un color en la semana y otro dentro del día.
const COLOR = {
    comida: 'border-brand/50',
    peri: 'border-[#6b5340]',
    entreno: 'border-brand',
    // Pautado: el naranja de la casa, como en la leyenda de la semana.
    cardio: 'border-brand/80',
    // Extra: el mismo naranja más flojo y con la línea cortada, que es un hueco reservado
    // y no una cita.
    extra: 'border-brand/40 border-dashed',
    sueno: 'border-border border-dashed',
};

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
               'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

/** «Lunes, 7 de septiembre», con el reloj del cliente y no con el del servidor. */
const comoSeLlamaElDia = (fecha) => {
    const d = new Date(`${fecha}T12:00:00`);
    if (Number.isNaN(d.getTime())) return fecha;
    const nombre = DIAS[d.getDay()];
    return `${nombre[0].toUpperCase()}${nombre.slice(1)}, ${d.getDate()} de ${MESES[d.getMonth()]}`;
};


/**
 * «¿ESTÁ LISTO TU MIÉRCOLES?» (Francisco, 15-09).
 *
 * Es lo que separa esta pantalla de Nutrición. Nutrición es «¿qué como?»: alimentos, gramos,
 * añadir, quitar, cuadrar, y casi siempre sobre hoy. Esta es «¿está listo?», y es sobre todo
 * para los días que vienen: el cliente no come nada aquí, comprueba si el miércoles está
 * preparado y entra a arreglarlo si no.
 *
 * Por eso NO SALE NI UN ALIMENTO. Salen estados -- la dieta sin terminar, sin horarios, día
 * de entreno, sin rutina --, y cada uno con su salida. Los macros salen también, pero como
 * estado («te faltan 49 P por poner»), no como las tres barras de trabajo.
 */
// LOS TRES COLORES DEL ESTADO, LOS DE LA CASA (Francisco, 15-09; su regla del 11-09:
// «verde si está válido, naranja si falta, rojo si se pasa»). Los puntos salían del naranja
// de la marca, que aquí no significa nada: el naranja de marca es el del botón y el logo, y
// el de «te falta» es otro tono propio. Son los mismos tokens que pintan el estado en
// Nutrición y en Mi semana, así que el mismo día se lee igual en las tres pantallas.
const TONO = {
    ok: 'bg-ok border-ok',
    falta: 'border-falta',
    sepasa: 'bg-sepasa border-sepasa',
};

const Punto = ({ estado, titulo, detalle, testid }) => (
    <div className="flex items-start gap-3" data-testid={testid}>
        {/* Relleno cuando esa parte está resuelta; hueco cuando falta, que es lo que dice
            «esto todavía está por hacer» sin gritar. */}
        <span className={`mt-1 w-3 h-3 rounded-full flex-shrink-0 border-2 ${TONO[estado] || TONO.falta}`} />
        <div className="min-w-0">
            <p className="text-sm font-bold text-foreground">{titulo}</p>
            {detalle && <p className="text-xs text-muted-foreground mt-0.5">{detalle}</p>}
        </div>
    </div>
);

// LOS MACROS DE ESTA PANTALLA VAN REDONDOS (Francisco, 16-09: «Mi horario aquí también
// aplica lo del redondeo»), igual que en Inicio y en Mi semana. Es la misma razón: esto es
// el día de un vistazo -- «49P · 24H · 10G» en cada fila de la hora --, y el gramo se afina
// en Nutrición, que está a un botón de aquí («Abrir la dieta»).
const num = (n) => num0(n);

const EstaListo = ({ listo, fecha, navigate }) => {
    if (!listo) return null;
    const { dieta, horarios, entreno, rutina, todo } = listo;

    // LA DIETA, CONTADA COMO ESTADO. Los cuatro estados son los del juez de la casa, el
    // mismo que pinta el verde de Mi semana y cuenta los días del reporte: aquí no se
    // vuelve a decidir nada, solo se escribe.
    const dietaTitulo = dieta.estado === 'completo' ? 'Dieta cuadrada'
        : dieta.estado === 'sin_crear' ? 'La dieta, sin empezar'
            : dieta.estado === 'se_pasa' ? 'La dieta se pasa'
                : 'La dieta, sin terminar';
    const f = dieta.faltan || {};
    // CON SU LETRA, como las filas de la hora y como Inicio (Francisco, 16-09: «devuelve
    // las letras a los macros»). Aqui iban sueltos -- «300 · 150 · 50» -- y en la misma
    // pantalla, tres dedos mas abajo, cada comida ya decia «49P · 22H · 10G».
    const dietaDetalle = dieta.estado === 'completo'
        ? `${num(dieta.objetivo?.P)}P · ${num(dieta.objetivo?.H)}H · ${num(dieta.objetivo?.G)}G`
        : dieta.estado === 'sin_crear' ? 'Todavía no has puesto nada en este día'
            : dieta.estado === 'se_pasa' ? 'Te pasas de lo que te toca: ajusta cantidades'
                : `Te faltan ${num(f.P)} P · ${num(f.H)} H · ${num(f.G)} G por poner`;

    const h = horarios || {};
    const horariosDetalle = h.ok
        ? `De ${h.desde} a ${h.hasta}`
        : h.con_hora ? `${h.con_hora} de ${h.total} comidas tienen hora`
            : 'Ninguna comida tiene hora';

    return (
        <div className="surface p-4 mt-5" data-testid="dia-esta-listo">
            <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-3">
                {todo ? `Tu ${comoSeLlamaElDia(fecha).split(',')[0].toLowerCase()} está listo`
                    : `¿Está listo tu ${comoSeLlamaElDia(fecha).split(',')[0].toLowerCase()}?`}
            </p>
            <div className="space-y-3">
                <Punto estado={dieta.estado === 'completo' ? 'ok'
                    : dieta.estado === 'se_pasa' ? 'sepasa' : 'falta'}
                    titulo={dietaTitulo} detalle={dietaDetalle} testid="listo-dieta" />
                <Punto estado={h.ok ? 'ok' : 'falta'}
                    titulo={h.ok ? `${h.total} comidas con hora` : 'Sin horarios'}
                    detalle={horariosDetalle} testid="listo-horarios" />
                {entreno?.es_de_entreno && (
                    <Punto estado="ok" titulo="Día de entreno"
                        detalle={entreno.hora
                            ? `A las ${entreno.hora}${entreno.con_peri ? ', con perientreno' : ''}`
                            : (entreno.con_peri ? 'Con perientreno' : 'Sin hora puesta')}
                        testid="listo-entreno" />
                )}
                {/* La rutina solo se echa en falta los días de entreno, y cuando está se
                    dice dentro de la línea del entreno: dos líneas para lo mismo sobran. */}
                {rutina?.hace_falta && !rutina.ok && (
                    <Punto estado="falta" titulo="Sin entreno asignado"
                        detalle="No tienes rutina puesta" testid="listo-rutina" />
                )}
            </div>

            {/* LAS SALIDAS. Solo las que hacen falta: un botón para arreglar algo que ya
                está bien es ruido. */}
            {/* LOS BOTONES, LOS DE LA CASA (Francisco, 15-09). Iban pequeños y sueltos, con
                un tamaño que no se usa en ninguna otra pantalla del cliente.
                Y CON LA LETRA BLANCA: el botón de la casa es naranja con texto blanco, que
                es lo que hay en Informes, en el alta y en el `.btn-primary` del CSS. La
                letra casi negra sobre naranja solo estaba en cuatro sitios y en ninguno de
                los que el cliente pisa a diario. */}
            {!todo && (
                <div className="flex gap-2 mt-4">
                    {!dieta.ok && (
                        <button type="button" data-testid="listo-ir-dieta"
                            onClick={() => navigate(`/dashboard/nutrition?date=${fecha}`)}
                            className="flex-1 rounded-xl bg-brand text-white font-bold py-3 hover:bg-brand/85 transition-colors">
                            Terminar la dieta
                        </button>
                    )}
                    {!h.ok && (
                        <button type="button" data-testid="listo-ir-horario"
                            onClick={() => navigate('/dashboard/semana/horario')}
                            className={`${dieta.ok ? 'flex-1 rounded-xl bg-brand text-white hover:bg-brand/85' : 'surface surface-hover'} font-bold py-3 px-4 text-sm transition-colors`}>
                            Poner mis horarios
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const MiDiaPage = () => {
    const { fecha } = useParams();
    const navigate = useNavigate();
    const { api } = useAuth();
    const [cargando, setCargando] = useState(true);
    const [dia, setDia] = useState(null);

    useEffect(() => {
        let vivo = true;
        (async () => {
            try {
                // CON SU RELOJ (15-09): el día que vive y la hora que es para él. De eso
                // depende si el botón de cerrar ese día sale o no, y midiéndolo con la hora
                // de España un cliente de Argentina veía el botón cuando el cierre lo iba a
                // rechazar, y no lo veía cuando sí podía.
                const hoy = new Date().toLocaleDateString('en-CA');
                const { data } = await api.get(
                    `/diets/horario/dia/${fecha}?hoy=${hoy}&hora_cliente=${new Date().getHours()}`);
                if (vivo) setDia(data);
            } catch {
                if (vivo) toast.error('No pudimos traer tu día');
            } finally {
                if (vivo) setCargando(false);
            }
        })();
        return () => { vivo = false; };
    }, [api, fecha]);

    if (cargando) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand border-t-transparent" />
            </div>
        );
    }

    const resumen = dia?.resumen || {};
    const horas = resumen.sueno_minutos != null
        ? `${Math.floor(resumen.sueno_minutos / 60)} h${resumen.sueno_minutos % 60
            ? ` ${resumen.sueno_minutos % 60}` : ''}`
        : null;
    const c = dia?.checkin;

    return (
        <div className="max-w-xl mx-auto px-4 lg:px-0 pb-28" data-testid="mi-dia">
            <button type="button" onClick={() => navigate('/dashboard/semana')}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3">
                <ChevronLeft className="w-4 h-4" /> Mi semana
            </button>

            <h1 className="titulo-pantalla">{comoSeLlamaElDia(fecha)}</h1>
            <p className="text-sm text-muted-foreground mt-1">Planifica tu día con antelación.</p>
            {/* «Día de entreno · toca Torso» (su artefacto). El grupo sale de la rutina
                estructurada o del reparto del PDF, la misma pareja de fuentes que la fila
                de la semana: el grupo no puede llamarse de una manera en la lista y de
                otra dentro del día. */}
            <p className="text-sm text-foreground mt-2" data-testid="dia-tipo">
                {dia?.tipo_dia === 'entrenamiento' ? 'Día de entreno' : 'Día de descanso'}
                {dia?.grupo ? ` · toca ${dia.grupo}` : ''}
            </p>

            {/* «¿ESTÁ LISTO TU MIÉRCOLES?», lo primero de todo y salga como salga el día.
                El día SIN horarios es justo el que hay que arreglar, así que aquí es donde
                más falta hace: antes, ese día solo tenía un cartel que llevaba al horario y
                no decía nada de la dieta ni del entreno. */}
            <EstaListo listo={dia?.listo} fecha={fecha} navigate={navigate} />

            {!dia?.puesto ? (
                /* El horario es lo único que esta pantalla dibuja hora a hora, así que sin
                   él se dice qué falta por ver, no que falte el día entero. */
                <p className="text-sm text-muted-foreground mt-5" data-testid="dia-sin-horario">
                    Cuando pongas las horas de tus comidas verás tu día entero aquí.
                </p>
            ) : (
                <>
                    {/* EL DÍA EN SILENCIO (regla 3 de la ficha 07). Se dice arriba y con
                        su motivo, que si no parece que la app se ha quedado muda sin más. */}
                    {resumen.silencio && (
                        <p className="flex items-center gap-2 text-sm text-muted-foreground mt-4"
                            data-testid="dia-en-silencio">
                            <BellOff className="w-4 h-4 flex-shrink-0" />
                            Este día lo pusiste en silencio: no te avisamos de nada.
                        </p>
                    )}

                    {/* LOS TRES NÚMEROS DE ARRIBA (ficha 21). El que no se sepa no se
                        inventa: se queda fuera en vez de salir a cero. */}
                    <div className="flex flex-wrap gap-x-8 gap-y-2 mt-4" data-testid="dia-resumen">
                        {horas && (
                            <div>
                                <p className="font-data text-xl font-bold text-foreground leading-none">{horas}</p>
                                <p className="text-xs text-muted-foreground mt-1">de sueño</p>
                            </div>
                        )}
                        {resumen.comidas > 0 && (
                            <div>
                                <p className="font-data text-xl font-bold text-foreground leading-none">{resumen.comidas}</p>
                                <p className="text-xs text-muted-foreground mt-1">comidas</p>
                            </div>
                        )}
                        {resumen.entreno && (
                            <div>
                                <p className="font-data text-xl font-bold text-foreground leading-none">{resumen.entreno}</p>
                                <p className="text-xs text-muted-foreground mt-1">entreno</p>
                            </div>
                        )}
                    </div>

                    {/* SU CHECK-IN DE ESE DÍA (ficha 22): «al abrir el día, lo que contestó».
                        Solo si lo cerró: al día que todavía no ha vivido no se le pone. */}
                    {c && (
                        <div className="surface p-3 mt-4 text-sm" data-testid="dia-checkin">
                            {/* «Tu check-in de ese día», con las palabras del artefacto
                                (Francisco, 12-09). Lo había cambiado por «Cómo te fue ese
                                día» por evitar la palabra check-in, y no me tocaba: el
                                literal es suyo y está escrito. */}
                            <span className="text-muted-foreground">Tu check-in de ese día: </span>
                            {/* `sensaciones` y no `mood` (12-09): la carita de mayo no la
                                escribe el cierre desde hace meses, así que aquí no salía
                                nunca ninguna estrella. */}
                            {c.sensaciones ? (
                                <span className="text-brand">
                                    {'★'.repeat(c.sensaciones)}{'☆'.repeat(5 - c.sensaciones)}
                                </span>
                            ) : null}
                            {[['descansó', c.descanso], ['energía', c.energia], ['hambre', c.hambre]]
                                .filter(([, v]) => v != null)
                                .map(([que, v]) => (
                                    <span key={que} className="text-muted-foreground"> · {que} {v}</span>
                                ))}
                        </div>
                    )}

                    {/* EL DÍA, HORA A HORA. La campana marca lo que le avisaría. */}
                    <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mt-6 mb-3">
                        Tu día, hora a hora
                    </p>
                    <div className="space-y-2" data-testid="dia-filas">
                        {(dia.filas || []).map((f, i) => (
                            <div key={i} className="flex items-start gap-3">
                                <p className="w-12 flex-shrink-0 text-xs font-data text-muted-foreground pt-3">
                                    {f.hora}
                                </p>
                                <div className={`flex-1 min-w-0 surface p-3 border ${COLOR[f.tipo] || 'border-border'}`}
                                    data-testid={`fila-${f.hora}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="text-sm font-bold text-foreground min-w-0">
                                            {f.titulo}{f.grupo ? ` · ${f.grupo}` : ''}
                                        </p>
                                        {f.avisa && <Bell className="w-3.5 h-3.5 text-brand flex-shrink-0 mt-0.5" />}
                                    </div>
                                    {/* «30P · 30H · 12G» (su artefacto). El peri lleva los
                                        suyos igual, que también es comida aunque no cuente
                                        como tal. La grasa se calla cuando es cero: en el
                                        intra siempre lo es y un «0G» no dice nada. */}
                                    {f.macros && (
                                        <p className="text-xs font-data text-muted-foreground mt-0.5"
                                            data-testid={`macros-${f.hora}`}>
                                            {num0(f.macros.P)}P · {num0(f.macros.H)}H
                                            {f.macros.G ? ` · ${num0(f.macros.G)}G` : ''}
                                        </p>
                                    )}
                                    {f.detalle && (
                                        <p className="text-xs text-muted-foreground mt-0.5">{f.detalle}</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* LA PUERTA PARA CERRAR ESE DÍA (Francisco, 12-09: «aún no sé cómo hacer esos
                check-ins en ningún día»). La pantalla enseñaba lo contestado y no daba
                manera de contestarlo: al cierre solo se llegaba desde la fila del Inicio o
                desde Seguimiento, y ninguna de las dos está aquí. Lleva la fecha puesta,
                que es lo que hace que se cierre ESE día y no el de hoy. */}
            {/* Solo cuando ese día se puede cerrar de verdad. Un día viejo ya no se
                cierra («se cerró a las 15:00 y ya no vuelve»), y un botón que lleva a una
                pantalla que va a decir que no es peor que no tener botón. */}
            {dia?.se_puede_cerrar && (
                <button type="button"
                    onClick={() => navigate(`/dashboard/checkins?fecha=${fecha}`)}
                    data-testid="dia-cerrar"
                    className={`w-full mt-6 rounded-xl py-3 font-bold transition-colors ${
                        dia?.checkin ? 'surface surface-hover text-foreground'
                            : 'bg-brand text-white hover:bg-brand/85'}`}>
                    {dia?.checkin ? 'Cambiar lo que contestaste' : '¿Cómo te fue este día?'}
                </button>
            )}

            {/* ABAJO, LA PUERTA A NUTRICIÓN, que es donde se toca la comida. Cuando el día
                ya está listo es el único botón que hace falta: «Abrir la dieta» y a otra
                cosa. Cuando falta algo, las salidas de arriba son las que llevan al sitio
                exacto, y aquí abajo se deja el paso a Nutrición y al horario igual, para
                quien viene a cambiar algo que ya estaba bien. */}
            <div className={`flex gap-2 ${dia?.se_puede_cerrar ? 'mt-2' : 'mt-6'}`}>
                <button type="button" onClick={() => navigate(`/dashboard/nutrition?date=${fecha}`)}
                    data-testid="dia-ver-dieta"
                    className="flex-1 rounded-xl bg-brand text-white font-bold py-3 hover:bg-brand/85 transition-colors">
                    {dia?.listo?.todo ? 'Abrir la dieta' : 'Ver la dieta de este día'}
                </button>
                <button type="button" onClick={() => navigate('/dashboard/semana/horario')}
                    data-testid="dia-cambiar-horario"
                    className="surface surface-hover px-4 py-3 text-sm font-bold text-foreground">
                    Mi horario
                </button>
            </div>
        </div>
    );
};

export default MiDiaPage;
